// The per-box jobs-at-once cap (`runners.max_slots`) and the bounds every door
// of it shares. Split out of `../runner.ts`, which is now the barrel over this
// directory.
//
// The bounds live in DOMAIN, not beside any one writer, because the cap is set
// through three doors and all three must refuse the same values: the
// `runners.max_slots` CHECK constraint in `@developerz/db`, the REST body
// (`PUT /v1/runners/:id/max-slots`) and the MCP variant (`box set_max_slots`)
// each derive their bound from these constants. The owner's undo for multi-job
// (`POST /v1/runners/max-slots/reset`, `box reset_max_slots`, the operator
// script) takes no value at all: it writes the floor, `RUNNER_MAX_SLOTS_MIN`, to
// every live box of an account. A bound kept
// beside one writer would let the others drift — the door schemas would then
// disagree with the CHECK about what is legal, and the disagreement would
// surface as a 500 off the write instead of a 400 off the door that saw the
// bad value.

import { z } from 'zod';

/**
 * The lowest the cap may go (#6740; epic #6552 v2 ruling V14,
 * docs/design/fleet-redesign/brief.md:391). One claim at a time is the floor —
 * a box that may take no work at all is drained, not zero-slotted.
 */
export const RUNNER_MAX_SLOTS_MIN = 1;

/**
 * The highest the cap may go. Shared verbatim by the db CHECK, the REST body
 * and the MCP variant — see the header.
 */
export const RUNNER_MAX_SLOTS_MAX = 8;

/**
 * What a box holds when nobody set a cap: the column is `NOT NULL DEFAULT 4`,
 * so there is NO unset state, never a null. The reset doors do NOT write this:
 * the owner's undo for multi-job writes {@link RUNNER_MAX_SLOTS_MIN} (1).
 */
export const RUNNER_MAX_SLOTS_DEFAULT = 4;

/**
 * The cap at every write boundary: a whole number of concurrent claims, within
 * the inclusive bounds. The stored column and every door derive from this one
 * schema, so a bound moves in exactly one place.
 */
export const RunnerMaxSlotsSchema = z
  .number()
  .int()
  .min(RUNNER_MAX_SLOTS_MIN)
  .max(RUNNER_MAX_SLOTS_MAX);
